/** Building blocks of the setup wizard (brand, drop zones, path fields, world rows, the texture mosaic, the join card). */
import { Check, Copy, CornerDownLeft, Link2, RefreshCw, Search, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { controller } from "../../app/controller.ts";
import { useApp } from "../../state/store.ts";
import { addExtraFolder, extraFolders, fileUrl, removeExtraFolder, type FoundMinecraft, type FoundWorld } from "../../lib/desktop.ts";
import { hasMaps } from "../../lib/share.ts";
import { useAccount } from "../../lib/auth.ts";
import { ROLE_LABEL } from "../../lib/collab.ts";
import { prefersReducedMotion } from "../../lib/onboarding.ts";
import { WorldTile } from "../Brand.tsx";

export function fmtSize(n: number) {
  return n > 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(n / 1e6))} MB`;
}

/** "today", "yesterday", "3 days ago", "3 weeks ago", "2 months ago", "last year" */
export function fmtDate(sec: number) {
  const d = new Date(sec * 1000);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  return days < 730 ? "last year" : d.toLocaleDateString();
}

/** Click to choose or drop files. `onDrop` gets the whole DataTransfer (so folders can be read too). */
export function DropZone(props: {
  accept: string;
  multiple?: boolean;
  onFiles: (f: File[]) => void;
  onDrop?: (dt: DataTransfer) => void;
  children: React.ReactNode;
  disabled?: boolean;
  big?: boolean;
  label?: string;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      className={`dropzone${props.big ? " big" : ""}${over ? " over" : ""}${props.disabled ? " disabled" : ""}${props.className ? " " + props.className : ""}`}
      role="button"
      tabIndex={props.disabled ? -1 : 0}
      aria-label={props.label}
      aria-disabled={props.disabled}
      onClick={() => !props.disabled && input.current?.click()}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !props.disabled) { e.preventDefault(); e.stopPropagation(); input.current?.click(); } }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        if (props.disabled) return;
        if (props.onDrop) props.onDrop(e.dataTransfer);
        else if (e.dataTransfer.files.length) props.onFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <input ref={input} type="file" hidden accept={props.accept} multiple={props.multiple} onChange={(e) => { if (e.target.files?.length) props.onFiles(Array.from(e.target.files)); e.target.value = ""; }} />
      {props.children}
    </div>
  );
}

/**
 * The world step's drop target: a dashed row ("Drop a world folder or .zip") with Folder… and Browse .zip… buttons.
 * Not a button itself (it holds two), so the drop is handled on the row and the buttons pick.
 */
export function DropRow({ disabled, roomy, onFiles, onDrop, onFolder }: { disabled: boolean; roomy?: boolean; onFiles: (f: File[]) => void; onDrop: (dt: DataTransfer) => void; onFolder: () => void }) {
  const zip = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      className={`wz-droprow${over ? " over" : ""}${disabled ? " disabled" : ""}${roomy ? " roomy" : ""}`}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(false); if (!disabled) onDrop(e.dataTransfer); }}
    >
      <input ref={zip} type="file" hidden accept=".zip" onChange={(e) => { if (e.target.files?.length) onFiles(Array.from(e.target.files)); e.target.value = ""; }} />
      <span className="wz-droprow-icon"><Upload size={roomy ? 18 : 16} aria-hidden="true" /></span>
      <span className="grow">{roomy ? <><b>Drop your world here</b><span>The world's folder, or a .zip of it. No zipping needed.</span></> : "Drop a world folder or .zip"}</span>
      <span className="wz-droprow-btns">
        <button type="button" className="btn btn-sm btn-ghost" disabled={disabled} onClick={onFolder}>Folder…</button>
        <button type="button" className="btn btn-sm btn-outline" disabled={disabled} onClick={() => zip.current?.click()}>Browse .zip…</button>
      </span>
    </div>
  );
}

/** A folder path in a read-only mono field with a Copy button (the jar / saves folder of the chosen launcher). */
export function PathField({ path, label, id, hint }: { path: string; label: string; id: string; hint?: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(path); } catch { field.current?.select(); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="wz-path">
      <label className="field-label" htmlFor={id}>{label}</label>
      <div className="field-box mono-box">
        <input id={id} ref={field} readOnly value={path} onFocus={(e) => e.currentTarget.select()} spellCheck={false} />
        <button type="button" className={`btn btn-sm btn-ghost${copied ? " copied" : ""}`} onClick={copy} aria-label={`Copy ${path}`}>
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}{copied ? "Copied" : "Copy"}
        </button>
      </div>
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

/**
 * The game's own textures, just read: a row of block tiles popping in one after the other (pixel-sharp, from the
 * jar on this computer). Shown under "Your blocks are here" the moment they arrive.
 */
export function BlockStrip({ count = 14 }: { count?: number }) {
  const [tiles, setTiles] = useState<string[]>([]);
  useEffect(() => {
    const sample = controller.textureSample(count);
    if (!sample) return;
    setTiles(sample.tiles.map((px) => {
      const cv = document.createElement("canvas");
      cv.width = cv.height = sample.size;
      cv.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(px), sample.size, sample.size), 0, 0);
      return cv.toDataURL();
    }));
  }, [count]);
  if (!tiles.length) return null;
  return (
    <div className="wz-blockstrip" aria-hidden="true">
      {tiles.map((src, i) => <img key={i} src={src} alt="" width={32} height={32} style={{ animationDelay: `${i * 55}ms` }} draggable={false} />)}
    </div>
  );
}

/** One world in a list: icon, name, a fact line, and when it was played. Clicking opens it; focus/hover selects it. */
export function WorldRow({ icon, name, sub, when, selected, disabled, className, autoFocus, title, onOpen, onSelect }: {
  icon?: string | null; name: string; sub: string; when?: string; selected: boolean; disabled: boolean; className?: string;
  autoFocus?: boolean; title?: string; onOpen: () => void; onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`wz-world${selected ? " selected" : ""}${className ? " " + className : ""}`}
      disabled={disabled}
      autoFocus={autoFocus}
      title={title}
      onClick={onOpen}
      onFocus={onSelect}
      onMouseEnter={onSelect}
    >
      {icon ? <img src={icon} alt="" width={46} height={46} /> : <WorldTile name={name} size={46} />}
      <span className="wz-world-text"><b>{name}</b><span>{sub}</span></span>
      <span className="wz-world-when">
        {when && <span>{when}</span>}
        {selected && !disabled && <span className="wz-open-hint"><kbd><CornerDownLeft size={11} aria-hidden="true" /></kbd>Open</span>}
      </span>
    </button>
  );
}

/** Desktop: worlds found in the launchers + folders the user added. */
export function DesktopWorlds({ found, disabled, onRescan, onOpen, selected, onSelect, focusFirst }: {
  found: FoundMinecraft; disabled: boolean; onRescan: () => void; onOpen: (w: FoundWorld) => void;
  selected: (path: string) => boolean; onSelect: (w: FoundWorld) => void; focusFirst: boolean;
}) {
  const [all, setAll] = useState(false);
  const [q, setQ] = useState("");
  const [path, setPath] = useState("");
  const [folders, setFolders] = useState(extraFolders());
  const filtered = q ? found.worlds.filter((w) => `${w.name} ${w.instance}`.toLowerCase().includes(q.toLowerCase())) : found.worlds;
  const list = all || q ? filtered : filtered.slice(0, 6);
  const add = () => { if (!path.trim()) return; addExtraFolder(path); setFolders(extraFolders()); setPath(""); onRescan(); };
  return (
    <div className="desktop-worlds">
      {(found.worlds.length > 6 || found.worlds.length === 0) && (
        <div className="wz-list-head">
          {found.worlds.length > 6
            ? <label className="field-box wz-search"><Search size={14} aria-hidden="true" /><input className="world-search" placeholder="Search worlds" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search worlds" /></label>
            : <span />}
          <button type="button" className="btn btn-sm btn-ghost" onClick={onRescan}><RefreshCw size={14} aria-hidden="true" />Look again</button>
        </div>
      )}
      {found.worlds.length === 0 && (
        <div className="wz-empty" role="status">
          <b>No worlds found on this computer</b>
          <span>{found.searched.length ? <>Searched {found.searched.join(", ")}. </> : null}Play a world once in your launcher, add its folder below, or drop it on this window.</span>
        </div>
      )}
      <div className="world-grid" role="listbox" aria-label="Worlds">
        {list.map((w, i) => (
          <WorldRow
            key={w.path}
            className="world-card"
            icon={w.icon ? fileUrl(w.icon) : null}
            name={w.name}
            sub={w.instance}
            when={`Played ${fmtDate(w.modified)}`}
            title={w.path}
            selected={selected(w.path)}
            disabled={disabled}
            autoFocus={focusFirst && i === 0 && !q}
            onOpen={() => onOpen(w)}
            onSelect={() => onSelect(w)}
          />
        ))}
      </div>
      {filtered.length > 6 && !q && <button type="button" className="btn btn-sm btn-ghost wz-more" onClick={() => setAll(!all)}>{all ? "Show fewer" : `Show all ${filtered.length}`}</button>}
      <details className="wz-disclose">
        <summary>Add a folder<span className="caption">Worlds on another drive, server backups…</span></summary>
        <form className="wz-inline-form" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <label className="field-box"><input placeholder="e.g. D:\Minecraft\saves or a world folder" value={path} onChange={(e) => setPath(e.target.value)} aria-label="Folder path" /></label>
          <button type="submit" className="btn btn-outline btn-lg">Add</button>
        </form>
        {folders.map((f) => (
          <div key={f} className="wz-folder-row"><code>{f}</code><button type="button" className="btn btn-sm btn-ghost" onClick={() => { removeExtraFolder(f); setFolders(extraFolders()); onRescan(); }}>Remove</button></div>
        ))}
        <p className="caption">Tip: open the folder in your file manager, click the address bar and copy the path.</p>
      </details>
    </div>
  );
}

/** Invite links open the website — paste one here to join inside the app (desktop) or from the welcome screen. */
export function JoinByLink({ disabled, autoFocus }: { disabled: boolean; autoFocus?: boolean }) {
  const [link, setLink] = useState("");
  const [bad, setBad] = useState(false);
  return (
    <form className="join-link" onSubmit={async (e) => { e.preventDefault(); setBad(!(await controller.joinFromLink(link.trim()))); }}>
      <label className={`field-box${bad ? " invalid" : ""}`}>
        <Link2 size={16} aria-hidden="true" />
        <input placeholder="Paste an invite link" value={link} autoFocus={autoFocus} onChange={(e) => { setLink(e.target.value); setBad(false); }} aria-label="Invite link" />
      </label>
      <button type="submit" className="btn btn-outline btn-lg" disabled={disabled || !link.trim()}>Join</button>
      {bad && <span className="field-error">That isn't an MC Planner invite link.</span>}
    </form>
  );
}

/** Guest: the invite card with the world, your name, textures and the Join button. */
export function JoinSection({ assetsReady, busy, textures }: { assetsReady: boolean; busy: boolean; textures: React.ReactNode }) {
  const invite = useApp((s) => s.invite)!;
  const savedName = useApp((s) => s.collab.name);
  const profileName = useAccount((s) => s.profile?.displayName);
  // signed in (worlds of accounts): your account's name — typing another one here renames the account
  const [name, setName] = useState((invite.account ? profileName || savedName : savedName || profileName) || "");
  const m = invite.manifest;
  const size = m ? m.files.reduce((a, f) => a + f.size, 0) : 0;
  const locked = !assetsReady && !(m && hasMaps(m));
  return (
    <section className={`join${locked ? " locked" : ""}`}>
      {!m && !invite.error && <p className="wz-quiet">Contacting the host's server…</p>}
      {invite.error && <p className="wz-note warn" role="status">{invite.error}</p>}
      {m && (
        <>
          <div className="join-card">
            <WorldTile name={m.name} size={46} />
            <span className="wz-world-text">
              <b>{m.name}</b>
              <span>
                Minecraft {m.version}{invite.account?.ownerName ? ` · shared by ${invite.account.ownerName}` : m.host ? ` · hosted by ${m.host}` : ""} · {fmtSize(size)} · updated {new Date(m.updatedAt).toLocaleDateString()}
                {invite.account && <> · you're {ROLE_LABEL[invite.account.role].toLowerCase()}</>}
              </span>
            </span>
          </div>
          {invite.account?.paused && <p className="wz-note warn">This world is paused (the owner's plan doesn't cover it). You can open it and look around, but not edit together.</p>}
          <p className="wz-quiet">
            Joining is free. The world streams from the host. Block textures come from Minecraft itself — your own game
            file, or Mojang's official download — never from other players.
          </p>
          {textures}
          <div className="wz-inline-form">
            <label className="field-box"><input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Your name" /></label>
            <button className="primary btn btn-primary btn-lg" disabled={!assetsReady || busy || !name.trim()} onClick={() => void controller.joinInvite(name.trim())}>Join</button>
          </div>
          {!assetsReady && hasMaps(m) && (
            <p className="wz-quiet">
              No jar at hand?{" "}
              <button className="link" disabled={busy || !name.trim()} onClick={() => void controller.joinInvite(name.trim(), { mapColours: true })}>
                Join with map colours only
              </button>{" "}
              — you see the host's map and can plan, without 3D blocks.
            </p>
          )}
        </>
      )}
      <button className="btn btn-sm btn-ghost wz-own" onClick={() => { useApp.getState().set({ invite: null, accountInvite: null }); history.replaceState(null, "", location.pathname); }}>Open my own world instead</button>
    </section>
  );
}
